import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { LayoutDashboard, Plus, Trash2, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';

const CustomKPIDashboard = ({ reducedMotion }) => {
  const [kpis, setKpis] = useState([
    { id: 1, name: 'Daily Appointments', value: 47, target: 50, trend: 'up', change: 12 },
    { id: 2, name: 'Answer Rate', value: 0.68, target: 0.75, trend: 'down', change: -3 },
    { id: 3, name: 'Avg Call Quality', value: 0.87, target: 0.85, trend: 'up', change: 5 }
  ]);

  const handleAddKPI = () => {
    toast.info('KPI builder opened');
  };

  const handleDeleteKPI = (id) => {
    setKpis(kpis.filter(k => k.id !== id));
    toast.success('KPI removed');
  };

  const formatValue = (value) => {
    if (value < 1) return `${(value * 100).toFixed(0)}%`;
    return value;
  };

  return (
    <Card className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 backdrop-blur-xl" data-testid="custom-kpi-dashboard-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl text-slate-100 flex items-center gap-2">
              <LayoutDashboard className="w-5 h-5 text-pink-400" />
              Custom KPI Dashboard
            </CardTitle>
            <CardDescription className="text-slate-400 mt-1">
              Build and track your own performance metrics
            </CardDescription>
          </div>
          <Button
            onClick={handleAddKPI}
            className="bg-pink-600 hover:bg-pink-700 text-white"
            size="sm"
            data-testid="add-kpi-button"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add KPI
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {kpis.map((kpi, index) => (
            <div
              key={kpi.id}
              className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50 hover:bg-slate-800/50 transition-all duration-200 relative"
              style={{
                animation: reducedMotion ? 'none' : `fadeIn 0.3s ease-out ${index * 0.05}s both`
              }}
              data-testid={`kpi-widget-${kpi.id}`}
            >
              {/* Delete Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeleteKPI(kpi.id)}
                className="absolute top-2 right-2 text-slate-400 hover:text-red-400 h-6 w-6 p-0"
                data-testid={`delete-kpi-${kpi.id}`}
              >
                <Trash2 className="w-3 h-3" />
              </Button>

              <div className="mb-4">
                <h4 className="text-sm font-medium text-slate-400 mb-2" data-testid="kpi-name">{kpi.name}</h4>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-slate-100" data-testid="kpi-value">{formatValue(kpi.value)}</p>
                  <div className="flex items-center gap-1">
                    {kpi.trend === 'up' ? (
                      <TrendingUp className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-red-400" />
                    )}
                    <span className={`text-sm font-semibold ${kpi.trend === 'up' ? 'text-emerald-400' : 'text-red-400'}`} data-testid="kpi-change">
                      {kpi.change > 0 ? '+' : ''}{kpi.change}%
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">Target</span>
                  <span className="text-slate-300" data-testid="kpi-target">{formatValue(kpi.target)}</span>
                </div>
                <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${kpi.value >= kpi.target ? 'bg-emerald-500' : 'bg-amber-500'}`}
                    style={{ width: `${Math.min((kpi.value / kpi.target) * 100, 100)}%` }}
                    data-testid="kpi-progress"
                  />
                </div>
              </div>
            </div>
          ))}

          {/* Add KPI Card */}
          <button
            onClick={handleAddKPI}
            className="p-4 bg-slate-800/20 rounded-lg border-2 border-dashed border-slate-700/50 hover:border-pink-500/50 hover:bg-slate-800/30 transition-all duration-200 flex flex-col items-center justify-center min-h-[150px]"
            data-testid="add-kpi-card"
          >
            <Plus className="w-8 h-8 text-slate-500 mb-2" />
            <p className="text-sm text-slate-400">Add Custom KPI</p>
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CustomKPIDashboard;